import type { ChangeEvent, FC, ReactElement } from 'react';

import { useAudioTrigger } from '../../../controllers/audioController/hooks';
import { Tooltip } from '../../tooltip/Tooltip';

import type { RadioGroupProps } from './types';

import styles from './RadioGroup.module.css';

/**
 * There's a radio group component that provides grouping of styled input type radio controls
 */

export const RadioGroup: FC<RadioGroupProps> = ({
  className = '',
  disabled = true,
  name,
  onChange,
  options,
  value,
}): ReactElement => {
  const audioTrigger = useAudioTrigger();

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    onChange?.(event.target.value);
    audioTrigger.play({ name: 'menuClick' });
  };

  let item,
    id = '';
  return (
    <fieldset data-testid={name} className={`${styles.radioGroup} ${className}`} disabled={disabled}>
      {options.map((option) => {
        id = `${name}-${option.value}`;
        item = (
          <>
            <input
              className={styles.radioGroup__input}
              defaultChecked={option.value === value}
              disabled={option?.disabled || disabled}
              hidden
              id={id}
              name={name}
              onChange={handleChange}
              type="radio"
              value={option.value}
            />
            <label data-testid={id} className={styles.radioGroup__label} htmlFor={id}>
              {option.label}
            </label>
          </>
        );

        return option?.tooltip ? (
          <Tooltip
            className={styles.radioGroup__option}
            disabled={option.tooltip.disabled}
            text={option.tooltip.text}
            key={option.value}
          >
            {item}
          </Tooltip>
        ) : (
          <div className={styles.radioGroup__option} key={option.value}>
            {item}
          </div>
        );
      })}
    </fieldset>
  );
};
